import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { formatDate_MM_DD_YYYY, showAlert } from '../../../../../Assests/Utility';
import { AppConstant } from '../../../../../Assests/AppConstant';
import { sagaActions } from '../../../../../../sagas/sagaActions';
import {
    getTTPaymentLogSelector,
    updateTTPaymentLogDetailList,
} from '../../../../../../redux/reducers/reducerSlices/TTPaymentLogReducer';
import { getRemoteConfigSelector } from '../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import DrilldownTableSS from '../../Dashboard/DefaultDashboard/DrillDownReports/Common/DrilldownTableSS';
import { CustomPaginationSS } from '../../../../Common/CustomPaginationSS/CustomPaginationSS';
import WarehouseSearch from '../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearch';
import DatePickerSS from '../../../../DatePickerSS/DatePickerSS';
import ModalSS from '../../../../SmartSchedule/SchedulePro/Components/Common/ModalSS/ModalSS';
import "react-datepicker/dist/react-datepicker.css";
import "../../../../../Components/DatePicker/DatePicker.css";

// Max characters shown inline before "Show More" link appears
const ERROR_MSG_TRUNCATE_LEN = 40;

/**
 * TTPaymentLogLoad
 * Displays TT Payment Transaction Logs from API: v2/reports/getPaymentTransactionLogs
 * Columns: Warehouse, Control Number, Zip, Payment Provider, Check Number, Auth Code,
 *          Amount, Payment Status, Error Message, Source Table, Transaction Date
 * Features: Start/End date filter (3-month max range), search, pagination,
 *           control-number detail popup, error-message "Show More" popup
 */
const TTPaymentLogLoad = ({ themeSelected = 'light-mode' }) => {
    const dispatch = useDispatch();

    const remoteConfig = useSelector(getRemoteConfigSelector);
    const { allConfigration } = remoteConfig;
    // Default 25 records per page; respects user selection from pagination component
    const defaultLimit = 25;

    const ttPaymentLogState = useSelector(getTTPaymentLogSelector);
    const {
        ttPaymentLogList,
        operationObj,
        isLoading,
        totalElements,
        ttPaymentLogDetailList,
        detailOperationObj,
        isDetailLoading,
    } = ttPaymentLogState;

    // ── Pagination state ──────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [currentLimit, setCurrentLimit] = useState(defaultLimit);
    const [isFirst, setIsFirst] = useState(true);
    const [isLast, setIsLast] = useState(false);

    // ── Search ────────────────────────────────────────────────────────────────
    const [searchStr, setSearchStr] = useState('');
    const firstSearchRender = useRef(true);

    // ── Date range ────────────────────────────────────────────────────────────
    const [selectedStartDate, setSelectedStartDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [selectedEndDate, setSelectedEndDate] = useState(() => {
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    });
    const [showStartDate, setShowStartDate] = useState(false);
    const [showEndDate, setShowEndDate] = useState(false);

    // Compute maxEndDate: min(today, startDate + 3 months)
    const computeMaxEndDate = (startDate) => {
        const today = new Date();
        const threeMonthsLater = new Date(startDate);
        threeMonthsLater.setMonth(startDate.getMonth() + 3);
        threeMonthsLater.setDate(threeMonthsLater.getDate() - 1);
        return today <= threeMonthsLater ? today : threeMonthsLater;
    };

    const [maxEndDate, setMaxEndDate] = useState(() => computeMaxEndDate(new Date()));

    const firstDateRender = useRef(true);

    // ── Control Number Detail Popup ───────────────────────────────────────────
    const [showDetailPopup, setShowDetailPopup] = useState(false);
    const [selectedControlNumber, setSelectedControlNumber] = useState(null);

    // ── Error Message "Show More" Popup ───────────────────────────────────────
    const [errorMessagePopup, setErrorMessagePopup] = useState(null);

    // ── Table column definitions ──────────────────────────────────────────────
    const tableTitleDD = [
        { id: 'Warehouse', name: 'Warehouse' },
        { id: 'Control', name: 'Control Number' },
        { id: 'Zip', name: 'Zip' },
        { id: 'PaymentProvider', name: 'Payment Provider' },
        { id: 'CheckNumber', name: 'Check Number' },
        { id: 'AuthCode', name: 'Auth Code (TT)' },
        { id: 'Amount', name: 'Amount' },
        { id: 'PaymentStatus', name: 'Payment Status' },
        { id: 'ErrorMessage', name: 'Error Message' },
        { id: 'SourceTable', name: 'Source Table' },
        { id: 'CreatedDate', name: 'Transaction Date' },
    ];

    // ── Fetch helpers ─────────────────────────────────────────────────────────
    const fetchData = (pageNo = 1, limit = defaultLimit, search = null) => {
        const endDate = selectedEndDate ? new Date(selectedEndDate) : null;
        if (endDate) endDate.setHours(23, 59, 59, 0);
        const payload = {
            StartDate: selectedStartDate
                ? moment(selectedStartDate).format(AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss)
                : null,
            EndDate: endDate
                ? moment(endDate).format(AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss)
                : null,
            Search: search && search.trim().length > 0 ? search.trim() : null,
            PageNo: pageNo,
            Limit: limit,
        };
        dispatch({
            type: sagaActions.FETCH_TT_PAYMENT_LOG,
            payload,
            operationObj: { type: 'Fetch TT Payment Log' },
        });
    };

    const fetchDetailByControlNumber = (controlNumber) => {
        dispatch({
            type: sagaActions.FETCH_TT_PAYMENT_LOG_BY_CONTROL_NUMBER,
            payload: { Control: controlNumber },
            operationObj: { type: 'Fetch TT Payment Log Detail' },
        });
    };

    // ── On mount ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchData(1, defaultLimit, null);
        return () => {
            dispatch(updateTTPaymentLogDetailList([]));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Date range change triggers re-fetch ───────────────────────────────────
    useEffect(() => {
        if (firstDateRender.current) {
            firstDateRender.current = false;
            return;
        }
        if (selectedStartDate && selectedEndDate) {
            setCurrentPage(1);
            fetchData(1, currentLimit, searchStr || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStartDate, selectedEndDate]);

    // ── Debounced search ──────────────────────────────────────────────────────
    useEffect(() => {
        if (firstSearchRender.current) {
            firstSearchRender.current = false;
            return;
        }
        const tout = setTimeout(() => {
            setCurrentPage(1);
            fetchData(1, currentLimit, searchStr || null);
        }, 1000);
        return () => clearTimeout(tout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchStr]);

    // ── Pagination bookkeeping when new list arrives ───────────────────────────
    useEffect(() => {
        if (operationObj?.type === 'Fetch TT Payment Log') {
            if (operationObj.status === true) {
                const totalPgs = Math.ceil(totalElements / currentLimit) || 1;
                setIsFirst(currentPage === 1);
                setIsLast(currentPage >= totalPgs || ttPaymentLogList.length < currentLimit);
            } else if (operationObj.status === false) {
                showAlert(toast.TYPE.ERROR, 'Unable to fetch payment transaction logs', themeSelected);
            }
            dispatch({ type: sagaActions.RESET_TT_PAYMENT_LOG_OPERATION_OBJ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationObj]);

    // ── Detail operation response ─────────────────────────────────────────────
    useEffect(() => {
        if (detailOperationObj?.type === 'Fetch TT Payment Log Detail') {
            if (detailOperationObj.status === false) {
                showAlert(toast.TYPE.ERROR, 'Unable to fetch logs for this control number', themeSelected);
            }
            dispatch({ type: sagaActions.RESET_TT_PAYMENT_LOG_DETAIL_OPERATION_OBJ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailOperationObj]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const pageChange = (pageNo, limit = defaultLimit) => {
        setCurrentPage(pageNo);
        setCurrentLimit(limit);
        fetchData(pageNo, limit, searchStr || null);
    };

    const handleStartDate = (date) => {
        const newMaxEnd = computeMaxEndDate(date);
        if (selectedEndDate && selectedEndDate > newMaxEnd) {
            setSelectedEndDate(null);
        }
        setSelectedStartDate(date);
        setMaxEndDate(newMaxEnd);
        setShowStartDate(false);
    };

    const handleControlNumberClick = (controlNumber) => {
        setSelectedControlNumber(controlNumber);
        setShowDetailPopup(true);
        fetchDetailByControlNumber(controlNumber);
    };

    const closeDetailPopup = () => {
        setShowDetailPopup(false);
        setSelectedControlNumber(null);
        dispatch(updateTTPaymentLogDetailList([]));
    };

    // ── Payment status badge ──────────────────────────────────────────────────
    const renderPaymentStatus = (status) => {
        if (!status) return <span className='color-black'>-</span>;
        const upper = status.toUpperCase();
        if (upper === 'SUCCESS' || upper === 'APPROVED') {
            return <span className='textGreen'>{status}</span>;
        } else if (upper === 'FAILED' || upper === 'DECLINED' || upper === 'ERROR') {
            return <span className='textRed'>{status}</span>;
        }
        return <span className='color-black'>{status}</span>;
    };

    // ── Error Message cell renderer ───────────────────────────────────────────
    const renderErrorMessage = (errorCode) => {
        if (!errorCode) return '-';
        if (errorCode.length <= ERROR_MSG_TRUNCATE_LEN) return errorCode;
        return (
            <span>
                {errorCode.substring(0, ERROR_MSG_TRUNCATE_LEN)}…{' '}
                <span
                    style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setErrorMessagePopup(errorCode)}
                >
                    Show More
                </span>
            </span>
        );
    };

    // ── Table data formatter (shared for detail popup) ────────────────────────
    const formatListData = (data) =>
        data.map((el, index) => ({
            id: el.ID || index,
            0: el.Warehouse || '-',
            1: el.Control || '-',
            2: el.ZipCode || '-',
            3: el.PaymentProvider || '-',
            4: el.CheckNumber || '-',
            5: el.AuthCode || '-',
            6: el.Amount !== undefined && el.Amount !== null
                ? `$${parseFloat(el.Amount).toFixed(2)}`
                : '-',
            7: renderPaymentStatus(el.PaymentStatus),
            8: renderErrorMessage(el.ErrorMessage),
            9: el.SourceTable || '-',
            10: el.CreatedDate
                ? `${moment(el.CreatedDate).format(AppConstant.commonStrings.MM_DD_YYYY_dateFormat)}\n${moment(el.CreatedDate).format(AppConstant.commonStrings.HH_mm_dateFormat)}`
                : AppConstant.commonStrings.hypen,
        }));

    // ── Main list formatter — Control Number as hyperlink, Error Message truncated ──
    const formatMainListData = (data) =>
        data.map((el, index) => ({
            id: el.ID || index,
            0: el.Warehouse || '-',
            1: el.Control ? (
                <span
                    style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                    onClick={() => handleControlNumberClick(el.Control)}
                >
                    {el.Control}
                </span>
            ) : '-',
            2: el.ZipCode || '-',
            3: el.PaymentProvider || '-',
            4: el.CheckNumber || '-',
            5: el.AuthCode || '-',
            6: el.Amount !== undefined && el.Amount !== null
                ? `$${parseFloat(el.Amount).toFixed(2)}`
                : '-',
            7: renderPaymentStatus(el.PaymentStatus),
            8: renderErrorMessage(el.ErrorMessage),
            9: el.SourceTable || '-',
            10: el.CreatedDate
                ? `${moment(el.CreatedDate).format(AppConstant.commonStrings.MM_DD_YYYY_dateFormat)}\n${moment(el.CreatedDate).format(AppConstant.commonStrings.HH_mm_dateFormat)}`
                : AppConstant.commonStrings.hypen,
        }));

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className=''>
            {/* ── Header row ──────────────────────────────────────────── */}
            <div className='d-flex justify-content-between align-items-center my-2 mx-3'>
                <div className='fw-bold'>TT Payment Log</div>
                <div className='d-flex justify-content-end align-items-end gap-2'>

                    {/* ── Date Range Pickers ──────────────────────────── */}
                    <div className='d-flex appt-report-header gap-3 align-items-center'>
                        {/* Start Date */}
                        <div className="pos-rel">
                            <div className='d-flex flex-column align-items-start'>
                                <label className='boldText m-0 f-14'>
                                    <i className="fa-regular fa-calendar"></i> Start Date
                                </label>
                                <button
                                    className='datePickerBtn h-100 w-100'
                                    onClick={() => { setShowStartDate(!showStartDate); setShowEndDate(false); }}
                                    disabled={isLoading}
                                >
                                    <span className="btn-title">
                                        {selectedStartDate ? formatDate_MM_DD_YYYY(selectedStartDate) : 'Start Date'}
                                    </span>
                                    <i className="fa-regular fa-calendar" style={{ marginInline: '9px' }}></i>
                                </button>
                                {showStartDate && (
                                    <div className="appt-summary-ss-datePicker">
                                        <DatePickerSS
                                            maxDate={new Date()}
                                            setSelectedDate={handleStartDate}
                                            selectedDate={selectedStartDate}
                                            closeCalendar={() => setShowStartDate(false)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* End Date */}
                        <div className="pos-rel">
                            <div className='d-flex flex-column align-items-start'>
                                <label className='boldText m-0 f-14'>
                                    <i className="fa-regular fa-calendar"></i> End Date
                                </label>
                                <button
                                    className='datePickerBtn h-100 w-100'
                                    onClick={() => { setShowEndDate(!showEndDate); setShowStartDate(false); }}
                                    disabled={isLoading}
                                >
                                    <span className="btn-title">
                                        {selectedEndDate ? formatDate_MM_DD_YYYY(selectedEndDate) : 'End Date'}
                                    </span>
                                    <i className="fa-regular fa-calendar" style={{ marginInline: '9px' }}></i>
                                </button>
                                {showEndDate && (
                                    <div className="appt-summary-ss-datePicker">
                                        <DatePickerSS
                                            minDate={selectedStartDate ? new Date(selectedStartDate) : undefined}
                                            maxDate={maxEndDate}
                                            setSelectedDate={(date) => {
                                                setSelectedEndDate(date);
                                                setShowEndDate(false);
                                            }}
                                            selectedDate={selectedEndDate}
                                            closeCalendar={() => setShowEndDate(false)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Search ──────────────────────────────────────── */}
                    <div style={{ width: '220px' }}>
                        <WarehouseSearch
                            placeholder='Search...'
                            inputValue={searchStr}
                            cancelSearch={() => setSearchStr('')}
                            onChangeHandler={(text) => setSearchStr(text)}
                        />
                    </div>
                </div>
            </div>

            {/* ── Table ───────────────────────────────────────────────── */}
            <div className='mt-2 appointmentListTable'>
                <DrilldownTableSS
                    themeSelected={themeSelected}
                    bodyData={
                        ttPaymentLogList && ttPaymentLogList.length
                            ? formatMainListData(ttPaymentLogList)
                            : []
                    }
                    titleData={tableTitleDD}
                    showCollapse={false}
                    showInput={false}
                    showSort={false}
                    sortKey={false}
                    drillDownReport={ttPaymentLogList}
                    initiateDrillDownReport={isLoading}
                    loaderArray={11}
                    showUTCTime={false}
                    isPaginated={true}
                />
            </div>

            {/* ── Pagination ───────────────────────────────────────────── */}
            {ttPaymentLogList && ttPaymentLogList.length ? (
                <CustomPaginationSS
                    pageChange={(pageNo, limit) => pageChange(pageNo, limit)}
                    pageNo={currentPage}
                    pageSize={currentLimit}
                    totalElements={totalElements}
                    totalPages={Math.ceil(totalElements / currentLimit) || 1}
                    last={isLast}
                    first={isFirst}
                    data={ttPaymentLogList}
                    isLoading={isLoading}
                />
            ) : (
                ''
            )}

            {/* ── Control Number Detail Popup ──────────────────────────── */}
            {showDetailPopup && (
                <ModalSS toggleModal={closeDetailPopup}>
                    <div className='p-3' style={{ minWidth: '80vw' }}>
                        <div className='mb-3'>
                            <h5 className='mb-0'>
                                Payment Logs — Control Number: <strong>{selectedControlNumber}</strong>
                            </h5>
                        </div>
                        <DrilldownTableSS
                            themeSelected={themeSelected}
                            bodyData={
                                ttPaymentLogDetailList && ttPaymentLogDetailList.length
                                    ? formatListData(
                                        [...ttPaymentLogDetailList].sort((a, b) =>
                                            new Date(b.CreatedDate) - new Date(a.CreatedDate)
                                        )
                                      )
                                    : []
                            }
                            titleData={tableTitleDD}
                            showCollapse={false}
                            showInput={false}
                            showSort={false}
                            sortKey={false}
                            drillDownReport={ttPaymentLogDetailList}
                            initiateDrillDownReport={isDetailLoading}
                            loaderArray={11}
                            showUTCTime={false}
                            isPaginated={false}
                        />
                    </div>
                </ModalSS>
            )}

            {/* ── Error Message "Show More" Popup ──────────────────────── */}
            {errorMessagePopup && (
                <ModalSS toggleModal={() => setErrorMessagePopup(null)}>
                    <div className='p-4' style={{ maxWidth: '600px', minWidth: '400px' }}>
                        <div className='mb-3'>
                            <h6 className='mb-0 fw-bold'>Error Message</h6>
                        </div>
                        <p className='mb-0' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {errorMessagePopup}
                        </p>
                    </div>
                </ModalSS>
            )}
        </div>
    );
};

export default TTPaymentLogLoad;